import { useEffect, useState } from 'react';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';

import type { Data } from '../../pages/best-companies/@dimension/+data.server';
import { getDimensionConfig } from '../../pages/best-companies/dimensionConfig';
import { useNewLocationSystemEnabled } from '../facets/new-location-system-dev-flag';
import { getBestCompaniesDimensionDataFromScript } from '../../pages/best-companies/@dimension/parseBestCompaniesDimensionInitialScript';
import { getVikeSerializedPageData } from '../vike/get-vike-serialized-page-data';
import {
  resolveBestCompaniesDimensionData,
  richerBestCompaniesDimensionPayload,
} from '../vike/resolve-best-companies-ssr-data';
import { fetchBestCompaniesDimensionFullFromApi } from './fetch-best-companies-dimension-full-from-api';

function payloadMatchesRoute(data: Data | undefined | null, routeDimension: string): data is Data {
  return Boolean(data?.config && data.dimensionSlug === routeDimension);
}

function pickPayloadForRoute(
  routeDimension: string,
  candidates: Array<Data | undefined | null>,
): Data | undefined {
  for (const c of candidates) {
    if (payloadMatchesRoute(c, routeDimension)) return c;
  }
  return undefined;
}

/**
 * Resolves best-companies dimension page data across SSR, serialized context, inline script,
 * and a same-origin API fallback when client navigation leaves `useData()` temporarily empty.
 *
 * Uses the same merge as best-companies dimension pages (`resolveBestCompaniesDimensionData`) so
 * `pageContext.data` from +data.server wins when the hook is empty during SSR.
 */
export function useBestCompaniesDimensionPagePayload(): {
  data: Data | undefined;
  /** True while waiting on client API after navigation when Vike data is not yet available. */
  isLoadingClientFallback: boolean;
} {
  // pageContext can be undefined during the first client render (same hydration gap
  // the best-companies hub and author-profile page work around) — every access below
  // must be optional-chained, not just the `.data` one.
  const pageContext = usePageContext() as
    | { data?: Data; routeParams?: { dimension?: string }; locale?: string }
    | undefined;
  const dataFromHook = useData<Data>();
  const dataFromContext = pageContext?.data;
  const pageContextLocale = pageContext?.locale;
  const routeDimensionParam = String(pageContext?.routeParams?.dimension ?? '').trim();
  const newLocationSystemEnabled = useNewLocationSystemEnabled();

  const serialized =
    typeof document === 'undefined'
      ? undefined
      : richerBestCompaniesDimensionPayload(
          getVikeSerializedPageData<Data>(),
          getBestCompaniesDimensionDataFromScript(),
        );

  const merged = resolveBestCompaniesDimensionData<Data>([dataFromHook, dataFromContext, serialized]);

  const routeDimension = routeDimensionParam || merged?.dimensionSlug || '';

  let resolved: Data | undefined;
  if (!routeDimension) {
    resolved = undefined;
  } else if (merged && payloadMatchesRoute(merged, routeDimension)) {
    resolved = merged;
  } else {
    const script = typeof document !== 'undefined' ? getBestCompaniesDimensionDataFromScript() : undefined;
    const win = typeof document !== 'undefined' ? getVikeSerializedPageData<Data>() : undefined;
    resolved = pickPayloadForRoute(routeDimension, [dataFromHook, dataFromContext, script, win]);
  }

  const hasResolved = resolved != null;

  const [clientFallback, setClientFallback] = useState<Data | null>(null);
  const [clientFetchFinished, setClientFetchFinished] = useState(false);

  useEffect(() => {
    setClientFallback(null);
    setClientFetchFinished(false);
  }, [routeDimension]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!routeDimension) return;
    if (hasResolved) return;

    const cfg = getDimensionConfig(routeDimension, newLocationSystemEnabled);
    if (!cfg) {
      setClientFetchFinished(true);
      return;
    }

    const controller = new AbortController();
    void fetchBestCompaniesDimensionFullFromApi({
      slug: routeDimension,
      dimensionType: cfg.type,
      locale: pageContextLocale || undefined,
      signal: controller.signal,
    })
      .then((payload) => {
        if (payload && payloadMatchesRoute(payload, routeDimension)) {
          setClientFallback(payload);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setClientFetchFinished(true);
      });

    return () => controller.abort();
  }, [hasResolved, pageContextLocale, routeDimension, newLocationSystemEnabled]);

  const data = resolved ?? clientFallback ?? undefined;
  const dimensionConfig = routeDimension ? getDimensionConfig(routeDimension, newLocationSystemEnabled) : undefined;
  const isLoadingClientFallback = Boolean(
    typeof window !== 'undefined' &&
      dimensionConfig &&
      !hasResolved &&
      !clientFetchFinished &&
      routeDimension,
  );

  return { data, isLoadingClientFallback };
}
